/**
 * Repair a native session whose cached technician state was lost.
 *
 * The native shell is the technician app, and both its rights and its session
 * flag live in the WebView cache — which can be gone while a Keychain/Keystore
 * credential is still able to revive the session. That revival can happen AFTER
 * the app has mounted: purgeZombieSessionIfNeeded() restores the refresh token
 * from the biometric vault on the login screen, and the next silent refresh puts
 * an access token in memory. A one-shot check at mount cannot see it, so the
 * session ends up authenticated with an empty permission list and no technician
 * flag, and the office shell renders with every menu group denied — a blank
 * screen with nothing on it to tap.
 *
 * `native` is passed in rather than imported so this module stays free of the
 * Capacitor runtime and can be unit-tested on plain node.
 */

let _inFlight = null
let _attempts = 0
let _gaveUpLogged = false
let _sessionGeneration = 0
let _retryEpoch = 0

/**
 * The reactive caller re-checks on every route change, so a gap that cannot be
 * closed (offline, or an account whose permission list really is empty) would
 * fire this pair of requests on every single navigation. Give the repair a small
 * budget instead; a new session gets a fresh one via resetNativeSessionRights().
 */
const MAX_ATTEMPTS = 3

/**
 * Why a native session needs its rights restored, or null when it is healthy.
 *
 * @param {{isAuthenticated?: boolean, user?: {id?: unknown}, permissions?: unknown, domain?: {id?: unknown}}} authStore
 * @param {boolean} native
 * @returns {string|null}
 */
export function nativeSessionRightsGap(authStore, native) {
  if (!native) return null
  if (!authStore?.isAuthenticated) return null
  if (!authStore.user?.id) return 'no cached user'
  if (authStore.permissionsHydrated === false || !Array.isArray(authStore.permissions)) {
    return 'no cached permissions'
  }
  if (!authStore.domain?.id) return 'no cached company'
  return null
}

/**
 * Restore the technician flag, and the profile plus permissions when they are
 * missing. Single-flight: concurrent callers await the same rights repair.
 *
 * `flagRestored` matters to the caller: `isTechnicianSession()` reads
 * localStorage, so the layout computed that depends on it is NOT reactive to
 * this write, and the router already sent this session to an office route.
 * Whoever restores the flag has to move the session to the technician app.
 *
 * @param {object} authStore
 * @param {boolean} native
 * @param {Storage} [storage]
 * @returns {Promise<{flagRestored: boolean, rightsRepaired: boolean}>}
 */
export async function repairNativeSessionRights(authStore, native, storage) {
  const result = { flagRestored: false, rightsRepaired: false }
  if (!native || !authStore?.isAuthenticated) return result
  const generation = _sessionGeneration
  const retryEpoch = _retryEpoch

  // A live session on the native shell is always a technician session: LoginView
  // forces technician mode there and hides the office toggle. Without the flag
  // the router stops redirecting to /technician and renders the office shell.
  // Callers do not await this function, so a storage failure must never surface
  // as an unhandled rejection.
  try {
    if (storage && storage.getItem('is_technician_session') !== 'true') {
      storage.setItem('is_technician_session', 'true')
      result.flagRestored = true
      console.warn('[NativeRights] Restored is_technician_session for a live native session')
    }
  } catch { /* storage unavailable — ignore */ }

  const gap = nativeSessionRightsGap(authStore, native)
  if (!gap) return result
  if (!_inFlight) {
    if (_attempts >= MAX_ATTEMPTS) {
      if (!_gaveUpLogged) {
        _gaveUpLogged = true // say it once, not on every navigation
        console.warn(`[NativeRights] Giving up after ${MAX_ATTEMPTS} attempts — session still has ${gap}`)
      }
      return result
    }
    _attempts += 1
    console.warn(`[NativeRights] Native session has ${gap} — restoring profile + permissions (attempt ${_attempts})`)
    // Assigned before the first await so a concurrent caller joins this run.
    const promise = (async () => {
      try {
        // refreshPermissions() needs a company, so restore the profile first.
        await authStore.hydrateTechnicianProfileAfterUnlock()
        // Logout/login may have replaced the session while the request was in
        // flight. Never continue an old repair under the new identity.
        if (generation !== _sessionGeneration || !authStore.isAuthenticated) return false
        if (authStore.permissionsHydrated === false || !Array.isArray(authStore.permissions)) {
          await authStore.refreshPermissions()
        }
      } catch (e) {
        console.warn('[NativeRights] Recovery failed:', e?.message || e)
      } finally {
        if (_inFlight?.promise === promise) _inFlight = null
      }
      // Report what the session actually looks like now, not that we tried.
      const repaired = generation === _sessionGeneration
        && nativeSessionRightsGap(authStore, native) === null
      if (repaired) {
        _attempts = 0
        _gaveUpLogged = false
      }
      return repaired
    })()
    _inFlight = { generation, retryEpoch, promise }
  }
  const pending = _inFlight
  result.rightsRepaired = await pending.promise
  const currentSessionNeedsOwnRepair = pending.generation !== _sessionGeneration
    && generation === _sessionGeneration
  const reopenedBudgetNeedsFreshAttempt = pending.retryEpoch !== _retryEpoch
    && retryEpoch === _retryEpoch
  if ((currentSessionNeedsOwnRepair || reopenedBudgetNeedsFreshAttempt)
      && authStore.isAuthenticated
      && nativeSessionRightsGap(authStore, native)) {
    // A new session arrived while the old repair was settling. Serialize the
    // two identities instead of overlapping their profile/permission requests,
    // then immediately give the current session its own repair.
    const current = await repairNativeSessionRights(authStore, native, storage)
    result.flagRestored = result.flagRestored || current.flagRestored
    result.rightsRepaired = current.rightsRepaired
  }
  return result
}

/**
 * Give the repair a fresh attempt budget. Called when the session ends, so the
 * next sign-in is not refused a repair because an earlier one kept failing.
 */
export function resetNativeSessionRights() {
  _sessionGeneration += 1
  _attempts = 0
  _gaveUpLogged = false
}

/**
 * Reopen retries for the same identity after reconnect/foreground without
 * invalidating or overlapping a repair that is already in flight.
 */
export function reopenNativeSessionRightsBudget() {
  _retryEpoch += 1
  _attempts = 0
  _gaveUpLogged = false
}
